import { useCallback, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import { createId } from '@paralleldrive/cuid2';
import type { Chart as IChart, Chat } from '@trinity/chat';
import { ChatAttachmentType, extractBrandInsightsIds } from '@trinity/chat';
import {
  ActiveChatSessionContext,
  AttachmentsContext,
} from '@trinity/chat-components/components/Chat/context';
import { isChartShapedWidgetType } from '../../utils/widget-chart';
import { useSelectedQueryParentIdentifier } from '../Chat/hooks/useSelectedQueryParentIdentifier';
import { useSubmitQueryMutation } from '../Chat/hooks/useSubmitQueryMutation';
import { ChartLegendContainer } from '../Chat/Message.Response.ChartContainer';
import { ChatMarkdown } from '../Markdown/Markdown';
import { ChartVisualizationUI } from '../Widgets/ChartWidgetRouter';
import { RepDashboardSubmitQueryContext } from '../Widgets/RepDashboardSubmitQueryContext';
import { RepDashboardWidget } from '../Widgets/RepDashboardWidget';
import { Chart } from './Chart';
import { GeneratedMessagesTable } from './GeneratedMessagesTable';
import { MessageTestingGridAttachment } from './MessageTestingGrid';
import { TableDataContainer } from './TableDataContainer';
import { WidgetChartContainer } from './WidgetChartContainer';
import { WidgetMetricCardContainer } from './WidgetMetricCardContainer';

type WidgetRepDashboardItemProps = {
  attachment: Chat.Attachment.WidgetRepDashboard;
};

const WidgetRepDashboardItem = ({ attachment }: WidgetRepDashboardItemProps) => {
  const { mutate } = useSubmitQueryMutation();
  const getSelectedQueryParentIdentifier = useSelectedQueryParentIdentifier();

  const handleSubmitQuery = useCallback(
    (query: string) => {
      mutate({
        identifier: createId(),
        parentQueryIdentifier: getSelectedQueryParentIdentifier(),
        query,
        action: null,
      });
    },
    [mutate, getSelectedQueryParentIdentifier]
  );

  return (
    <RepDashboardSubmitQueryContext.Provider value={handleSubmitQuery}>
      <RepDashboardWidget data={attachment.data} config={{ title: '', ...attachment.config }} />
    </RepDashboardSubmitQueryContext.Provider>
  );
};

type Props = {
  id: string;
};

export const Item = (props: Props) => {
  const attachments = useContext(AttachmentsContext);
  const attachment = useMemo(() => attachments.find((x) => x.id === props.id), [attachments, props.id]);

  const chat = useContext(ActiveChatSessionContext);
  const projectId = chat?.context ? (extractBrandInsightsIds(chat.context).projectId ?? 0) : 0;

  switch (attachment?.type) {
    case ChatAttachmentType.Chart: {
      const { values, ...other } = attachment.data;

      const item = {
        ...other,
        data: values,
      } as IChart['chart'];

      const defaultLegendOpen = item.legend?.defaultOpen ?? false;

      return (
        <ChartLegendContainer defaultValue={defaultLegendOpen}>
          <Chart item={item} type={attachment.data.type} />
        </ChartLegendContainer>
      );
    }

    case ChatAttachmentType.Table: {
      return <TableDataContainer attachment={attachment} />;
    }

    case ChatAttachmentType.WidgetChart: {
      // only support widget types render the button bar
      if (isChartShapedWidgetType(attachment.data.type)) {
        return <WidgetChartContainer attachment={attachment} projectId={projectId} />;
      }
      return (
        <ChartWidgetContainer>
          <ChartVisualizationUI chartData={attachment.data} />
        </ChartWidgetContainer>
      );
    }

    case ChatAttachmentType.WidgetText: {
      return <ChatMarkdown value={attachment.data.content} />;
    }

    case ChatAttachmentType.WidgetMetricCard: {
      return <WidgetMetricCardContainer attachment={attachment} projectId={projectId} />;
    }

    case ChatAttachmentType.WidgetRepDashboard: {
      return <WidgetRepDashboardItem attachment={attachment} />;
    }

    case ChatAttachmentType.MessageTestingGrid: {
      return <MessageTestingGridAttachment id={props.id} />;
    }

    case ChatAttachmentType.GeneratedMessages: {
      return <GeneratedMessagesTable id={props.id} />;
    }

    default: {
      console.error(`Unsupported attachment type: ${attachment?.type}`);
      return null;
    }
  }
};

Item.displayName = 'Chat.Attachments.Item';

const ChartWidgetContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});
